import type { CardString, GameState } from '../../lib';
import {
	calculateCardPoints,
	getCardObjectFromString,
	getCardsInHand,
	getRankValue,
	getTrickWinner,
	Rank,
	RoundStage,
	Suit,
} from '../../lib';

// Evaluation from the bot's perspective. The search minimizes the bot's points,
// so this returns the bot's *points* (lower is better). `search.ts` negates it
// into a utility for alpha-beta.

// Phantom penalty for dangerous cards still in the bot's hand at the search
// horizon, so the bot sheds them early and avoids being forced to take points
// later. The spade term covers the Q♠ 13-point swing; beyond that:
//  - hearts held are guaranteed future point risk, and high hearts are hard to
//    duck without winning the trick, so they are weighted by rank;
//  - a high club/diamond in a *short* suit (<=2 cards) can be forced to win
//    that suit's trick and capture hearts / the Q♠, so it is mildly penalized.
// This cuts the bot's variance tail (forced wins) without adding search depth.
function handDanger(state: GameState, botPos: number): number {
	let danger = 0;
	const hand = getCardsInHand(state.deck, botPos);

	const suitCount = new Map<Suit, number>();

	for (const c of hand) {
		suitCount.set(c.suit, (suitCount.get(c.suit) ?? 0) + 1);
	}

	for (const c of hand) {
		if (c.suit === Suit.Spades) {
			if (c.rank === Rank.Queen) {
				danger += 8;
			} else if (c.rank === Rank.Ace || c.rank === Rank.King) {
				danger += 2;
			}
		} else if (c.suit === Suit.Hearts) {
			danger += 0.5 + (getRankValue(c.rank) - 2) * 0.1;
		} else if ((c.rank === Rank.Ace || c.rank === Rank.King) && (suitCount.get(c.suit) ?? 0) <= 2) {
			danger += 2;
		}
	}

	return danger;
}

// Exact points the bot has already taken (cards in its winnings pile).
function wonPoints(state: GameState, botPos: number): number {
	return calculateCardPoints(state.deck)[botPos] ?? 0;
}

// Points currently sitting in the open, unfinished trick that the bot would be
// forced to take if it is the present winner.
function openTrickExposure(state: GameState, botPos: number): number {
	const round = state.rounds.at(-1);
	const trick = round?.tricks.at(-1);

	if (!trick) {
		return 0;
	}

	const played = trick.cardsPlayed.filter((c): c is CardString => typeof c === 'string');

	if (played.length === 0) {
		return 0;
	}

	const winner = getTrickWinner(trick);

	if (winner !== botPos) {
		return 0;
	}

	let exposure = 0;

	for (const c of played) {
		const card = getCardObjectFromString(c);

		if (card.suit === Suit.Hearts) {
			exposure += 1;
		} else if (card.suit === Suit.Spades && card.rank === Rank.Queen) {
			exposure += 13;
		}
	}

	return exposure;
}

export function evaluate(state: GameState, botPos: number): number {
	const stage = state.roundStage;

	if (stage === RoundStage.RoundEnd || stage === RoundStage.GameOver) {
		return wonPoints(state, botPos);
	}

	return wonPoints(state, botPos) + openTrickExposure(state, botPos) + handDanger(state, botPos);
}
